/**
 * The sections of a model page. Inside a project they are chosen from the sidebar, which links
 * to `?tab=<id>`; the page reads the same ids, so the two cannot drift apart.
 *
 * `manageOnly` sections are for people who manage the model's project: its status belongs to the
 * link between the model and the project, so Settings exists only inside a project, for its admins.
 */
export const MODEL_SECTIONS = [
  { id: 'overview', label: 'Overview & health' },
  { id: 'model', label: 'Model & instances' },
  { id: 'families', label: 'Families' },
  { id: 'sheets', label: 'Sheets' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'audits', label: 'Audits' },
  { id: 'members', label: 'Members' },
  { id: 'settings', label: 'Settings', manageOnly: true },
] as const;

export type ModelSectionId = (typeof MODEL_SECTIONS)[number]['id'];

/**
 * What a model is made of. A BIM model has a 3D model and its instances; a CAD model is a set of
 * drawings, so the same section is called Drawing and shows the project's drawings instead.
 */
export const MODEL_KINDS = ['BIM', 'CAD'] as const;
export type ModelKind = (typeof MODEL_KINDS)[number];
export const DEFAULT_MODEL_KIND: ModelKind = 'BIM';

export const MODEL_KIND_LABEL: Readonly<Record<ModelKind, string>> = {
  BIM: 'BIM (3D model)',
  CAD: 'CAD (drawings)',
};

/** The sections a person is offered, named for the kind of model. */
export function visibleModelSections(
  canManage: boolean,
  kind: ModelKind = DEFAULT_MODEL_KIND
): readonly { readonly id: ModelSectionId; readonly label: string }[] {
  return MODEL_SECTIONS.filter((section) => canManage || !('manageOnly' in section)).map((section) => ({
    id: section.id,
    label: section.id === 'model' && kind === 'CAD' ? 'Drawing' : section.label,
  }));
}

/** The section a `?tab=` value opens; an old `instances` link opens the model section. */
export function modelSectionOf(tab: string | null, hasInstance: boolean): ModelSectionId {
  if (tab === 'instances') return 'model';
  const known = MODEL_SECTIONS.find((section) => section.id === tab);
  if (known !== undefined) return known.id;
  return hasInstance ? 'model' : 'overview';
}
